import type { ComponentProps } from "react";
import { Outlet } from "react-router";
import { AppShell, TopbarPending } from "@/components/layout/app-shell";
import { useDelayed, ViewError } from "@/components/page-states";
import { useSessionError } from "@shared/client/session";
import { useView } from "@shared/client/view";
import { useT } from "@shared/client/locale";

type ShellData = Omit<ComponentProps<typeof AppShell>, "children" | "pending">;

const NO_SHELL: ShellData = { email: "", name: "" };

/**
 * Cadre de l'espace produit (ex-layout serveur de /app). Le rail et la topbar
 * s'affichent sans attendre la coque : des repères neutres tiennent la place
 * de l'offre et du compte pendant qu'elle se lit, et la page charge sa vue en
 * même temps. `session="pending"` : le même cadre, rendu par la garde tant
 * que la session est inconnue — sans la page, dont l'accès n'est pas encore
 * établi, mais avec l'erreur si la session n'a pas pu être lue. Rien pendant
 * les 180 premières ms : un visiteur déconnecté, ou pas encore passé par
 * l'onboarding, part vers sa page sans entrevoir l'espace. La garde rend les
 * deux au même endroit : le cadre ne se démonte pas en passant de l'un à
 * l'autre.
 */
export default function ProductLayout({ session = "ready" }: { session?: "pending" | "ready" }) {
  const ready = session === "ready";
  const sessionError = useSessionError();
  const shell = useView<ShellData>(ready ? "/api/views/app-shell" : null);
  const late = useDelayed();
  const t = useT();
  if (!ready && !late && !sessionError) return null;
  const topbar = !ready ? (sessionError ? "hidden" : "loading") : shell.error ? "failed" : "loading";

  return (
    <AppShell {...(shell.data ?? NO_SHELL)} pending={shell.data ? undefined : <TopbarPending state={topbar} />}>
      {ready ? <Outlet /> : sessionError ? <ViewError error={sessionError} subject={t.common.states.yourWorkspace} /> : null}
    </AppShell>
  );
}
